import * as THREE from 'three';
import { activeLook } from './look.js';

/**
 * The lighting model, layered on three's Lambert shader so every lit object shares it:
 *
 *  - wrap lighting: light reaches a little past the terminator, so shapes read as soft
 *    and rounded instead of half-black (global patch, zero cost)
 *  - rim light: a thin sky-coloured edge on characters, trees, rocks and buildings
 *  - cloud shadows: slow-drifting shadow patches from a noise field
 *  - valley mist: fog that pools in low ground at dawn, dusk and night
 *  - caustics: rippling light on the lake and sea beds
 *  - colour grading: ACES plus a little saturation and a cool-shadow / warm-highlight split
 *
 * All of it is a handful of ALU ops per pixel; nothing needs an extra render pass.
 */
export const lightUniforms = {
  uHMTime: { value: 0 },
  uHMCloud: { value: 0.35 },
  uHMMist: { value: 0.2 },
  uHMMistBase: { value: 0.3 },
  uHMRim: { value: new THREE.Color(0.3, 0.35, 0.45) },
  uHMSun: { value: new THREE.Color(1, 1, 1) },
};

let patched = false;
/** One-time edits to three's shared shader chunks. Call before the first render. */
export function patchShaderChunks() {
  if (patched) return;
  patched = true;
  const G = activeLook().grade, n = (v: number) => v.toFixed(4), v3 = (a: number[]) => `vec3( ${a.map(n).join(', ')} )`;
  const C = THREE.ShaderChunk as Record<string, string>;
  C.lights_lambert_pars_fragment = C.lights_lambert_pars_fragment.replace(
    'float dotNL = saturate( dot( geometryNormal, directLight.direction ) );',
    `float dotNL = saturate( ( dot( geometryNormal, directLight.direction ) + ${n(G.wrap)} ) / ${n(1 + G.wrap)} );`,
  );
  C.tonemapping_pars_fragment = C.tonemapping_pars_fragment.replace(
    'vec3 CustomToneMapping( vec3 color ) { return color; }',
    `vec3 CustomToneMapping( vec3 color ) {
      vec3 c = ACESFilmicToneMapping( color );
      float l = dot( c, vec3( 0.2126, 0.7152, 0.0722 ) );
      c = mix( vec3( l ), c, ${n(G.sat)} );
      c += ${v3(G.shadowTint)} * ( 1.0 - l ) * ( 1.0 - l ) + ${v3(G.highTint)} * l * l;
      c = saturate( c );
      c = c * c * ( 3.0 - 2.0 * c ) * ${n(G.contrast)} + c * ${n(1 - G.contrast)};
      return saturate( c );
    }`,
  );
}

const NOISE = /* glsl */ `
  float hmHash( vec2 p ) { vec3 p3 = fract( vec3( p.xyx ) * 0.1031 ); p3 += dot( p3, p3.yzx + 33.33 ); return fract( ( p3.x + p3.y ) * p3.z ); }
  float hmNoise( vec2 p ) {
    vec2 i = floor( p ), f = fract( p ); vec2 u = f * f * ( 3.0 - 2.0 * f );
    return mix( mix( hmHash( i ), hmHash( i + vec2( 1, 0 ) ), u.x ), mix( hmHash( i + vec2( 0, 1 ) ), hmHash( i + vec2( 1, 1 ) ), u.x ), u.y );
  }
  float hmFbm( vec2 p ) { return hmNoise( p ) * 0.6 + hmNoise( p * 2.1 + 3.7 ) * 0.3 + hmNoise( p * 4.3 + 7.1 ) * 0.1; }
  float hmCaustic( vec2 p, float t ) {
    vec2 q = p;
    q += vec2( sin( q.y * 1.3 + t * 0.9 ), cos( q.x * 1.1 - t * 0.7 ) ) * 0.7;
    q += vec2( sin( q.y * 2.1 - t * 1.1 ), cos( q.x * 1.9 + t * 1.3 ) ) * 0.35;
    float v = sin( q.x * 2.0 ) * sin( q.y * 2.0 );
    return pow( 1.0 - abs( v ), 7.0 );
  }
`;

export interface StyleOpts { rim?: boolean; caustics?: boolean; clouds?: boolean; /** The terrain: fine grain and broad patches painted in, rock strata on steep ground. */ ground?: boolean }

/** Give a Lambert material the shared lighting model. Safe to call twice; composes with wind. */
export function stylize(m: THREE.Material, opts: StyleOpts = {}) {
  if (!(m as THREE.MeshLambertMaterial).isMeshLambertMaterial || m.userData.hmStyled) return m;
  m.userData.hmStyled = true;
  const { rim = true, caustics = false, clouds = true, ground = false } = opts;
  const prev = m.onBeforeCompile;
  const baseKey = m.customProgramCacheKey();
  m.onBeforeCompile = (shader: { uniforms: Record<string, unknown>; vertexShader: string; fragmentShader: string }, renderer: THREE.WebGLRenderer) => {
    prev.call(m, shader as never, renderer);
    Object.assign(shader.uniforms, lightUniforms);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying vec3 vHMWorld;\nvoid main() {')
      .replace('#include <fog_vertex>', `#include <fog_vertex>
        vec4 hmW = vec4( transformed, 1.0 );
        #ifdef USE_INSTANCING
          hmW = instanceMatrix * hmW;
        #endif
        vHMWorld = ( modelMatrix * hmW ).xyz;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `uniform float uHMTime, uHMCloud, uHMMist, uHMMistBase;
        uniform vec3 uHMRim, uHMSun;
        varying vec3 vHMWorld;
        ${NOISE}
        void main() {`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        ${ground ? `{
          // detail that fades with distance (no shimmer far off): grain, clumps and broad patches
          float hmD = length( vViewPosition );
          float hmNear = 1.0 - smoothstep( 14.0, 46.0, hmD );
          vec2 hmP = vHMWorld.xz;
          // keep the lattice off the world grid (a slight turn), so nothing lines up with the tiles
          vec2 hmR = mat2( 0.8, -0.6, 0.6, 0.8 ) * hmP;
          float grain = hmNoise( hmR * 13.7 ) * 0.5 + hmNoise( hmR * 29.3 + 4.0 ) * 0.5;
          float clump = hmNoise( hmR * 2.3 + 11.0 ) * 0.6 + hmNoise( hmR * 4.9 + 2.0 ) * 0.4;
          float patchN = hmFbm( hmR * 0.09 + 3.0 );
          float lum = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
          float greenish = smoothstep( 0.0, 0.08, diffuseColor.g - max( diffuseColor.r, diffuseColor.b ) * 0.9 );
          vec3 c = diffuseColor.rgb;
          // a fine, faint grain up close, and gentle clumps: texture you feel more than see
          c *= 1.0 + ( grain - 0.5 ) * 0.12 * hmNear;
          c *= 1.0 + ( clump - 0.5 ) * 0.1 * hmNear * greenish;
          // drier, yellower patches in the grass; darker damp ones (broad and soft)
          c = mix( c, c * vec3( 1.08, 1.03, 0.84 ), smoothstep( 0.55, 0.85, patchN ) * 0.22 * greenish );
          c = mix( c, c * vec3( 0.88, 0.93, 0.88 ), smoothstep( 0.45, 0.15, patchN ) * 0.18 * greenish );
          // rock: strata lines across steep faces
          // the smooth (interpolated) normal, so steep ground darkens smoothly rather than facet by facet
          vec3 hmN = inverseTransformDirection( normalize( vNormal ), viewMatrix );
          float steep = smoothstep( 0.82, 0.6, abs( hmN.y ) );
          float strata = sin( vHMWorld.y * 9.0 + hmNoise( hmP * 0.9 ) * 4.0 ) * 0.5 + 0.5;
          c *= 1.0 - steep * strata * 0.16;
          diffuseColor.rgb = max( c, vec3( 0.0 ) ) + lum * 0.0;
        }` : ''}`)
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
        ${clouds ? `reflectedLight.directDiffuse *= 1.0 - uHMCloud * smoothstep( 0.45, 0.8, hmFbm( vHMWorld.xz * 0.04 + uHMTime * vec2( 0.011, 0.005 ) ) );` : ''}
        ${rim ? `{ float hmRim = pow( 1.0 - saturate( dot( normal, normalize( vViewPosition ) ) ), 3.0 );
          reflectedLight.indirectDiffuse += hmRim * uHMRim * ( diffuseColor.rgb + 0.15 ); }` : ''}
        ${caustics ? `if ( vHMWorld.y < 0.05 ) {
          float hmDepth = max( -vHMWorld.y, 0.0 );
          float hmC = hmCaustic( vHMWorld.xz * 1.5, uHMTime ) * exp( -hmDepth * 0.9 ) * smoothstep( 0.0, 0.25, hmDepth + 0.05 );
          reflectedLight.directDiffuse += hmC * 0.55 * uHMSun * diffuseColor.rgb;
          reflectedLight.directDiffuse *= mix( 1.0, 0.7, saturate( hmDepth * 0.6 ) );
        }` : ''}`)
      .replace('#include <fog_fragment>', `#ifdef USE_FOG
          float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );
          float hmMist = uHMMist * exp( -max( vHMWorld.y - uHMMistBase, 0.0 ) * 0.9 ) * smoothstep( 4.0, 30.0, vFogDepth );
          hmMist *= 0.75 + 0.5 * hmNoise( vHMWorld.xz * 0.08 + uHMTime * 0.02 );
          fogFactor = max( fogFactor, min( hmMist, 0.7 ) );
          gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
        #endif`);
  };
  m.customProgramCacheKey = () => `${baseKey}|hm${rim ? 'r' : ''}${caustics ? 'c' : ''}${clouds ? 'k' : ''}${ground ? 'g' : ''}`;
  return m;
}
